import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Label, Token} from '@primer/react'
import {useCallback, useMemo, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {commitClearProjectItemFieldValueMutation} from '../../../mutations/clear-project-item-field-value'
import {commitUpdateProjectItemFieldValueMutation} from '../../../mutations/update-project-item-field-value'
import type {
  IssueViewerSingleSelectFieldValueNode,
  ProjectSingleSelectFieldNode,
  ProjectSingleSelectFieldOptionNode,
  ProjectSingleSelectFieldValueNode,
} from '../project-item-section-fields-query-types'
import {FieldWrapper} from './FieldWrapper'
import {type BaseFieldProps, getTokenStyle} from './Shared'
import styles from './SingleSelectField.module.css'
import {SingleSelectLeadingVisual, SingleSelectToken} from './SingleSelectToken'

const AUTO_ASSIGN_STATUS_LABEL = 'Auto-assign to agent'
const AUTO_ASSIGN_STATUS_ACCESSIBLE_DESCRIPTION = 'This status automatically assigns the issue to an agent.'
const EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS = new Set<string>()

export type SingleSelectFieldConfigFragment = RelayCompat<Pick<ProjectSingleSelectFieldNode, 'id' | 'name' | 'options'>>

export type SingleSelectFieldOptionFragment = Pick<
  ProjectSingleSelectFieldOptionNode,
  'id' | 'optionId' | 'name' | 'nameHTML' | 'color' | 'descriptionHTML'
>

export type SingleSelectFieldFragment = Pick<
  ProjectSingleSelectFieldValueNode,
  'id' | 'optionId' | 'name' | 'nameHTML' | 'color'
> &
  Pick<IssueViewerSingleSelectFieldValueNode, 'id' | 'optionId' | 'name' | 'color'>

type SingleSelectFieldProps = BaseFieldProps<SingleSelectFieldConfigFragment, SingleSelectFieldFragment> & {
  autoAssignWorkflowOptionIds?: ReadonlySet<string>
}

export function SingleSelectField({
  viewerCanUpdate,
  itemId,
  projectId,
  field,
  value: valueData,
  onIssueUpdate,
  isStatusField,
  autoAssignWorkflowOptionIds = EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS,
}: SingleSelectFieldProps) {
  const [showInput, setShowInput] = useState(false)
  const environment = useRelayEnvironment()

  const fieldId = field.id
  const fieldName = field.name

  const [filter, setFilter] = useState('')

  const onSelectionChange = useCallback(
    (selection: SingleSelectFieldOptionFragment[]) => {
      const selectedOption = selection[0]
      if (selection.length === 1 && selectedOption) {
        commitUpdateProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
            value: {singleSelectOptionId: selectedOption.optionId},
          },
          optimisticFieldValue: {
            id: `optimistic-field-value-${itemId}-${fieldId}`,
            field: {id: fieldId},
            optionId: selectedOption.optionId,
            name: selectedOption.name,
            nameHTML: selectedOption.nameHTML,
            color: selectedOption.color,
          },
          onCompleted: onIssueUpdate,
        })
      } else if (selection.length === 0) {
        commitClearProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
          },
          onCompleted: onIssueUpdate,
        })
      }
      setShowInput(false)
    },
    [environment, fieldId, itemId, onIssueUpdate, projectId],
  )

  const filterItems = useCallback((v: string) => {
    setFilter(v)
  }, [])

  const items = useMemo(() => {
    const options = field.options || []
    // RELAY-CLEANUP(SingleSelectFieldConfigFragment): color enum is open in the still-Relay parent.
    // Remove cast when SingleSelectFieldConfigFragment leaves Relay.
    const typedOptions = options as unknown as SingleSelectFieldOptionFragment[]
    if (!filter) return typedOptions
    return typedOptions.filter(l => l.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [field.options, filter])

  const selectedItems = useMemo(() => {
    // RELAY-CLEANUP(SingleSelectFieldConfigFragment): color enum is open in the still-Relay parent.
    // Remove cast when SingleSelectFieldConfigFragment leaves Relay.
    const options = (field.options || []) as unknown as SingleSelectFieldOptionFragment[]
    return options.filter(i => i.optionId === valueData?.optionId)
  }, [field.options, valueData?.optionId])

  const itemPickerCacheKey = useMemo(() => {
    return isStatusField ? Array.from(autoAssignWorkflowOptionIds).sort().join(',') : ''
  }, [autoAssignWorkflowOptionIds, isStatusField])

  const convertToItemProps = useCallback(
    (option: SingleSelectFieldOptionFragment): ExtendedItemProps<SingleSelectFieldOptionFragment> => {
      const hasAutoAssignWorkflow = isStatusField && autoAssignWorkflowOptionIds.has(option.optionId)
      const autoAssignDescriptionId = hasAutoAssignWorkflow ? `auto-assign-status-${option.id}` : undefined

      return {
        ...(autoAssignDescriptionId ? {'aria-describedby': autoAssignDescriptionId} : {}),
        id: option.optionId,
        children: (
          <div className={styles.optionContent}>
            <span className={styles.optionTitle}>
              <span className={styles.optionName}>
                <VerifiedHTMLText
                  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point.
                  html={option.nameHTML as SafeHTMLString}
                />
              </span>
              {hasAutoAssignWorkflow && <Label className={styles.autoAssignLabel}>{AUTO_ASSIGN_STATUS_LABEL}</Label>}
            </span>
            {hasAutoAssignWorkflow && (
              <span id={autoAssignDescriptionId} className="sr-only">
                {AUTO_ASSIGN_STATUS_ACCESSIBLE_DESCRIPTION}
              </span>
            )}
            <VerifiedHTMLText
              // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point.
              html={option.descriptionHTML as SafeHTMLString}
              className={styles.VerifiedHTMLText}
            />
          </div>
        ),
        source: option,
        leadingVisual: () => <SingleSelectLeadingVisual inputColor={option.color} />,
      }
    },
    [autoAssignWorkflowOptionIds, isStatusField],
  )

  const valueDataColor = valueData?.color
  const valueDataName = valueData?.name

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      return (
        <FieldWrapper
          showInput={showInput}
          setShowInput={setShowInput}
          name={fieldName}
          isStatusField={isStatusField}
          placeholder={
            isStatusField ? (
              <div className={styles.statusPlaceholder}>
                {LABELS.emptySections.status}
                <TriangleDownIcon />
              </div>
            ) : (
              <>{LABELS.emptySections.singleSelectFilterPlaceholder}</>
            )
          }
          value={
            valueDataName && valueDataColor ? (
              <div className={styles.selectedValueContainer}>
                <SingleSelectToken inputName={valueDataName} inputColor={valueDataColor} />
                {isStatusField && <TriangleDownIcon />}
              </div>
            ) : null
          }
          anchorProps={anchorProps}
        />
      )
    },
    [fieldName, isStatusField, showInput, valueDataColor, valueDataName],
  )

  if (!viewerCanUpdate)
    return (
      <FieldWrapper
        showInput={showInput}
        setShowInput={setShowInput}
        canUpdate={false}
        name={fieldName}
        placeholder={LABELS.emptySections.noValue(fieldName)}
        value={
          valueData?.name ? (
            <Token className={getTokenStyle(valueData.name)} size="medium" text={valueData.name} />
          ) : null
        }
        isStatusField={isStatusField}
      />
    )

  return (
    <ItemPicker
      key={itemPickerCacheKey}
      items={items}
      initialSelectedItems={selectedItems}
      filterItems={filterItems}
      getItemKey={i => i.optionId}
      convertToItemProps={convertToItemProps}
      placeholderText={LABELS.emptySections.singleSelectFilterPlaceholder}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      renderAnchor={renderAnchor}
      height={'large'}
      cssAnchorPositioningSettings={{fallbackStrategy: 'opposite-side'}}
    />
  )
}
